import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton, Amount,
} from '@redpanda/ui';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';
import { recordInvoicePayment } from '../services/api';
import { Invoice, outstandingOf, updateInvoiceInLists } from '../stores/invoiceStore';
import { useBankAccountOptionsStore } from '../stores/bankAccountStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { todayInput, toUtcMidnight, dateInputValue } from '../utils/dates';

interface InvoicePaymentDialogProps {
  invoice: Invoice;
  onClose: () => void;
}

interface FormData {
  date: string;
  amount: string;
  bankAccountId: string;
}

/**
 * 登記收款（同 Xero、QuickBooks 的 Record payment）：可分次收款
 * 後端同時新增銀行存入交易與「借銀行存款／貸應收帳款」分錄；收滿自動變「已收款」
 */
const InvoicePaymentDialog: React.FC<InvoicePaymentDialogProps> = ({ invoice, onClose }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const bankAccounts = useBankAccountOptionsStore(state => state.accounts).filter(account => account.isActive);
  const fetchBankAccounts = useBankAccountOptionsStore(state => state.fetchAll);
  const outstanding = outstandingOf(invoice);
  const today = todayInput();
  const invoiceDate = dateInputValue(invoice.date);

  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch } = useForm<FormData>({
    defaultValues: { date: today < invoiceDate ? invoiceDate : today, amount: String(outstanding), bankAccountId: '' },
  });
  const amountInput = useNumberInput('decimal');

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);
  // 預設第一個帳戶（使用者沒有選過才帶入）
  useEffect(() => {
    if (!watch('bankAccountId') && bankAccounts.length > 0) setValue('bankAccountId', bankAccounts[0].id);
  }, [bankAccounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving, onClose);

  const onSubmit = async (data: FormData) => {
    setSaving(true);
    try {
      const { data: saved } = await recordInvoicePayment(invoice.id, {
        date: toUtcMidnight(data.date),
        amount: Number(data.amount),
        bankAccountId: data.bankAccountId,
      });
      updateInvoiceInLists(invoice.id, saved);
      toast.success(t('invoices.payment.success'));
      notifyDataChanged('invoices');
      notifyDataChanged('bankTransactions');
      notifyDataChanged('journals');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'common.messages.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-lg">
        <DialogHeader title={t('invoices.payment.title')} onClose={handleClose} closeDisabled={saving} closeLabel={t('common.actions.close')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            <p className="text-sm text-fg-secondary">
              {invoice.number} · {invoice.customerName} · {t('invoices.payment.outstanding')} <span className="font-medium text-fg"><Amount value={outstanding} /></span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormFieldWithError label={t('invoices.payment.date')} required error={errors.date?.message} htmlFor="paymentDate">
                <Input
                  id="paymentDate"
                  type="date"
                  {...register('date', {
                    required: t('common.forms.validation.required'),
                    validate: value => value >= invoiceDate || t('errors.PAYMENT_BEFORE_INVOICE'),
                  })}
                  variant={errors.date ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('invoices.payment.amount')} required error={errors.amount?.message} htmlFor="paymentAmount">
                <Input
                  id="paymentAmount"
                  inputMode="decimal"
                  className="text-right tabular-nums"
                  {...amountInput.bind(register('amount', {
                    validate: value => {
                      const amount = Number(value);
                      if (!(amount > 0)) return t('bankTransactions.dialog.amountMin');
                      return amount <= outstanding + 0.005 || t('errors.PAYMENT_EXCEEDS_BALANCE');
                    },
                  }))}
                  variant={errors.amount ? 'error' : 'default'}
                />
              </FormFieldWithError>
            </div>
            <FormFieldWithError
              label={t('invoices.payment.bankAccount')}
              required
              error={errors.bankAccountId?.message}
              helpText={bankAccounts.length === 0 ? t('invoices.payment.noBankAccount') : undefined}
              htmlFor="paymentBank"
            >
              <CustomSelect
                id="paymentBank"
                value={watch('bankAccountId')}
                onChange={value => setValue('bankAccountId', value, { shouldDirty: true, shouldValidate: true })}
                placeholder={t('invoices.payment.selectBankAccount')}
                options={bankAccounts.map(account => ({ value: account.id, label: `${account.accountName}（${account.bankName}）` }))}
              />
              <input type="hidden" {...register('bankAccountId', { required: t('invoices.payment.selectBankAccount') })} />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} disabled={bankAccounts.length === 0} className={dialogButtonClass.primary}>
              {t('invoices.payment.submit')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default InvoicePaymentDialog;
